import { images } from "@/assets/img/imagesMap";
import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import ModalButtonContainer from "./ModalButtonContainer";
import LoadingSpiner from "../ui/LoadingSpiner";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { X } from "lucide-react";
import BtnSuccess from "../ui/button/btn-success";
import BtnCancel from "../ui/button/btn-cancle";
import { LargeSolidButton } from "../ui/button/large-modal-buttons";

// ─── 공유 인프라 ────────────────────────────────────────────────────────────────

function getContainerRect(): DOMRect | null {
  const el = document.querySelector(".c_main__container");
  return el ? el.getBoundingClientRect() : null;
}

function getPortalRoot() {
  return typeof document !== "undefined"
    ? document.querySelector("main")
    : null;
}

/** Dialog 전용: scroll-lock + overlay 위치 계산 */
function useDialogOverlay(isOpen: boolean): React.CSSProperties {
  const [overlayRect, setOverlayRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const main = document.querySelector(".c_main__container");
    if (!main) return;
    main.classList.add("overflow-hidden");
    const update = () => setOverlayRect(getContainerRect());
    update();
    main.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      main.classList.remove("overflow-hidden");
      main.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      setOverlayRect(null);
    };
  }, [isOpen]);

  const rect = overlayRect ?? getContainerRect();
  return rect
    ? {
        position: "fixed",
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      }
    : { position: "fixed", inset: 0 };
}

// ─── CustomModal.Confirm ─────────────────────────────────────────────────────
// 2버튼 확인 다이얼로그 (예/아니오)

type ConfirmProps = {
  isOpen?: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  title?: string | React.ReactNode;
  isPending?: boolean;
  pendingMsg?: string;
  confirmText?: string;
  cancelText?: string;
  showActions?: boolean;
  confirmButtonClassName?: string;
  cancelButtonClassName?: string;
  /** 내용 컨테이너 클래스 재정의 (특수 케이스용) */
  contentClassName?: string;
  className?: string;
  children?: React.ReactNode;
  /** 헤더 바 사용 여부 */
  header?: boolean;
  /** header=true일 때 헤더 타이틀 */
  headerTitle?: string;
};

function Confirm({
  isOpen = true,
  onClose,
  onConfirm,
  title,
  isPending = false,
  pendingMsg = "로딩중...",
  confirmText = "예",
  cancelText = "아니오",
  showActions = true,
  confirmButtonClassName,
  cancelButtonClassName,
  contentClassName,
  className,
  children,
  header,
  headerTitle,
}: ConfirmProps) {
  const overlayStyle = useDialogOverlay(isOpen);

  if (!isOpen) return null;

  const portalRoot = getPortalRoot();
  const handleOverlayClose = () => {
    if (!isPending) onClose();
  };
  const handleConfirm = onConfirm ?? (() => {});

  const content = (
    <div
      className="z-50 flex items-center justify-center bg-[#1D1D1DCC] bg-opacity-60"
      style={overlayStyle}
      onClick={handleOverlayClose}
    >
      <div
        className={cn(
          "relative max-h-[calc(100vh-100px)]  max-w-[calc(100vw-400px)] scrollbar-float overflow-y-auto rounded-[10px] bg-white shadow-lg",
          contentClassName,
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {isPending ? (
          <div className="px-[46px] py-[30px]">
            <LoadingSpiner msg={pendingMsg} />
          </div>
        ) : (
          <>
            {header && (
              <header className="py-[13px]  px-10 bg-accent flex items-center justify-center rounded-tl-[10px] rounded-tr-[10px] relative">
                {headerTitle && (
                  <h3 className="font-pretendard text-foreground font-semibold text-lg select-none">
                    {headerTitle}
                  </h3>
                )}
                <button type="button" onClick={onClose}>
                  <img
                    src={images.icon_thin_close}
                    alt=""
                    aria-hidden="true"
                    className="absolute size-7 top-[1px] right-[2px] hover:opacity-70"
                  />
                </button>
              </header>
            )}
            {title && (
              <div className="mb-[19px] pt-6 px-10 text-base text-foreground whitespace-pre-line text-center font-pretendard">
                {title}
              </div>
            )}
            <div className="px-10 pb-5">
              {children && <div className="pt-5">{children}</div>}
              {showActions && (
                <ModalButtonContainer
                  onClose={onClose}
                  onConfirm={handleConfirm}
                  cancelText={cancelText}
                  confirmText={confirmText}
                  confirmButtonClassName={confirmButtonClassName}
                  cancelButtonClassName={cancelButtonClassName}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return portalRoot ? createPortal(content, portalRoot) : content;
}

// ─── CustomModal.Alert ───────────────────────────────────────────────────────
// 1버튼 알림 다이얼로그 (확인)

function Alert({
  isOpen = true,
  onClose,
  title,
  confirmText = "확인",
  dismissOnOverlay = true,
  contentClassName,
  className,
  children,
}: {
  isOpen?: boolean;
  onClose: () => void;
  title?: string | React.ReactNode;
  confirmText?: string;
  dismissOnOverlay?: boolean;
  contentClassName?: string;
  className?: string;
  children?: React.ReactNode;
}) {
  const overlayStyle = useDialogOverlay(isOpen);

  if (!isOpen) return null;

  const portalRoot = getPortalRoot();

  const content = (
    <div
      className="z-50 flex items-center justify-center bg-[#1D1D1DCC] bg-opacity-60"
      style={overlayStyle}
      onClick={dismissOnOverlay ? onClose : undefined}
    >
      <div
        className={cn(
          "relative bg-white rounded-[10px] px-[38px] pt-[20px] pb-[24px] shadow-lg max-h-[90vh] max-w-[min(100%,580px)] overflow-y-auto",
          contentClassName,
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="mb-[12px] whitespace-pre-wrap text-center font-pretendard text-[14px] text-black">
            {title}
          </div>
        )}
        {children}
        <div className="flex justify-center gap-3">
          <BtnSuccess
            type="button"
            variant="default"
            onClick={onClose}
            className="flex items-center justify-center  bg-sig  text-white transition-colors hover:bg-sig/90"
          >
            <span className="whitespace-nowrap font-pretendard text-[14px]">
              {confirmText}
            </span>
          </BtnSuccess>
        </div>
      </div>
    </div>
  );

  return portalRoot ? createPortal(content, portalRoot) : content;
}

// ─── CustomModal.Panel ───────────────────────────────────────────────────────
// 대형 패널 모달 (absolute inset-0, 콘텐츠 크기 기반)

function Panel({
  onClose,
  isLoading = false,
  loadingMsg,
  dismissOnOverlay = true,
  className,
  children,
  header,
  headerTitle,
  button,
  btnSuccessText = "저장",
  btnCancelText = "닫기",
  btnSuccessOnClick = onClose,
  btnCancelOnClick = onClose,
  btnSuccessDisabled = false,
  oneButton = false,
  btnText = "저장",
}: {
  onClose: () => void;
  isLoading?: boolean;
  loadingMsg?: string;
  dismissOnOverlay?: boolean;
  header?: boolean;
  headerTitle?: string;
  /** 패널 컨테이너 클래스 재정의
   * - 기본: w-[90%] bg-[#F0F0F2]
   * - 흰 배경: className="bg-white border border-[#D6D6DE]"
   * - 넓이 fit: className="w-fit min-w-[900px]"
   * - 소형 다이얼로그: className="w-auto max-h-none rounded-[10px] shadow-lg"
   */
  className?: string;
  children: React.ReactNode;
  button?: boolean;
  btnSuccessText?: string;
  btnCancelText?: string;
  btnSuccessOnClick?: () => void;
  btnCancelOnClick?: () => void;
  btnSuccessDisabled?: boolean;
  oneButton?: boolean;
  btnText?: string;
}) {
  const portalRoot = getPortalRoot();
  const overlayStyle = useDialogOverlay(true);

  const content = (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center"
      style={overlayStyle}
    >
      <div
        className="absolute inset-0 bg-[#1D1D1DCC] bg-opacity-60 pointer-events-auto"
        onClick={dismissOnOverlay ? onClose : undefined}
      />

      <div
        className={cn(
          "relative flex flex-col  max-w-[calc(100vw-300px)] max-h-[calc(100vh-140px)] bg-background rounded-lg overflow-hidden",
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading && (
          <div className="flex absolute inset-0 z-10 bg-background/50 items-center justify-center">
            <div className="bg-background p-5 px-10 rounded-lg shadow-2xl">
              <LoadingSpiner msg={loadingMsg} />
            </div>
          </div>
        )}
        <div className="overflow-y-auto scrollbar-float">
          {header && (
            <header className="py-[13px] sticky top-0  z-9 px-10 bg-accent flex items-center justify-center rounded-tl-[10px] rounded-tr-[10px]">
              {headerTitle && (
                <h3 className="font-pretendard text-foreground font-semibold text-xl select-none">
                  {headerTitle}
                </h3>
              )}

              <Button
                className="absolute right-5 group top-1/2 -translate-y-1/2"
                variant="ghost"
                onClick={onClose}
              >
                <X className="size-6 text-foreground/50 group-hover:text-foreground" />
              </Button>
            </header>
          )}
          <div className="py-5 px-10">{children}</div>

          {oneButton && (
            <div className="p-5 pt-0! sticky flex justify-center bottom-0 bg-background">
              <div className="absolute bottom-full w-full left-0  z-10 h-10 pointer-events-none bg-gradient-to-b from-transparent to-background" />
              <LargeSolidButton onClick={btnSuccessOnClick}>
                <span>{btnText}</span>
              </LargeSolidButton>
            </div>
          )}

          {button && (
            <div className="p-5 pt-0! pb-5 sticky bottom-0 bg-background">
              {/* bg Gradient */}
              <div className="absolute bottom-full w-full left-0  z-10 h-10 pointer-events-none bg-gradient-to-b from-transparent to-background" />
              <div className="flex justify-center font-pretendard gap-2 items-center">
                <BtnCancel onClick={btnCancelOnClick}>
                  {btnCancelText}
                </BtnCancel>
                <BtnSuccess
                  type="button"
                  onClick={btnSuccessOnClick}
                  disabled={btnSuccessDisabled}
                >
                  {btnSuccessText}
                </BtnSuccess>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return portalRoot ? createPortal(content, portalRoot) : content;
}

// ─── 컴파운드 export ──────────────────────────────────────────────────────────

const CustomModal = { Confirm, Alert, Panel };
export default CustomModal;
